// =========================================================================
// SECURITY CAMERA INSTALLATION — GOOGLE ADS LANDING PAGE
// (/camera-installation)
// =========================================================================
//
// Conversion-focused page for "security camera installation tampa" style
// searches. Same recipe as ManagedItServicesLandingPage in
// ../SightlineWebsite.tsx: one primary action (request a camera site survey),
// kit components shared with the other landing pages, and every visible
// string passed through t() with its Spanish translation kept in the shared
// dictionary src/i18n/es.ts (keyed by the exact English source string).
//
// Copy rules for this page: frame cameras as visibility, documentation,
// remote monitoring, and loss-prevention SUPPORT — never as crime
// prevention, and never with fabricated stats or testimonials.

import { useT } from "../i18n";
import type { PageKey } from "../i18n";
import {
  CtaButton,
  LeadForm,
  MobileStickyCta,
  ServiceHero,
  TrustBar,
  IndustryGrid,
  WhyChooseUs,
  ServiceArea,
  LeadCta,
  SectionLabel,
  FeatureCard,
  trackCta,
} from "../SightlineWebsite";
import {
  MapPin,
  ClipboardCheck,
  Camera,
  Smartphone,
  DoorOpen,
  Car,
  Warehouse,
  Store,
  RefreshCw,
  HardDrive,
  Network,
  Wrench,
  ChevronRight,
} from "lucide-react";

/** Single source of truth for the FAQ accordion AND the FAQPage JSON-LD. */
export const cameraFaqs: Array<{ q: string; a: string }> = [
  {
    q: "Do you install cameras in existing buildings?",
    a: "Yes. Most of our camera work is in buildings that are already occupied and operating. We use existing cable pathways wherever possible and schedule the work around your hours so the disruption to your team is minimal.",
  },
  {
    q: "Can you upgrade or replace an existing camera system?",
    a: "In most cases, yes. We evaluate your existing cameras, recorder, and cabling, then recommend whether to reuse the equipment, replace it, or extend the current system. If the existing cabling is in good condition, it can often be reused.",
  },
  {
    q: "Can I view cameras from my phone?",
    a: "Yes. We configure remote viewing on your phone, tablet, or computer so you can check live and recorded video from anywhere with an internet connection. We walk you through the app and access setup as part of the installation.",
  },
  {
    q: "How long do cameras keep footage?",
    a: "It depends on the recorder, the number of cameras, and the recording quality. Local NVR systems like the ones we install typically store 30-90 days of footage, and we size the storage to match how long your business needs to keep recordings.",
  },
  {
    q: "Which camera brands do you install?",
    a: "We work with UniFi Protect, Hikvision, Axis Communications, Dahua, Amcrest, and Reolink. We recommend a brand based on your site, your budget, and the systems you want the cameras to integrate with, and we explain the trade-offs before you commit.",
  },
  {
    q: "Do you run the cabling too?",
    a: "Yes. We install and terminate the structured cabling for every camera run, along with the switches and network equipment the cameras depend on. That keeps one accountable team for the cameras and the network behind them.",
  },
];


export function CameraInstallationLanding({
  setPage,
}: {
  setPage: (page: PageKey) => void;
}) {
  const t = useT();

  const scrollToLeadForm = () => {
    document
      .getElementById("camera-installation-lead-form")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const trustItems: Array<{ icon: typeof MapPin; label: string }> = [
    { icon: MapPin, label: "Local Tampa Bay Support" },
    { icon: ClipboardCheck, label: "Free On-Site Site Survey" },
    { icon: Camera, label: "Network-Based IP Cameras" },
    { icon: Smartphone, label: "Remote Viewing Setup" },
  ];

  const whatWeInstall: Array<{ icon: typeof MapPin; title: string; text: string }> = [
    {
      icon: DoorOpen,
      title: "Entry & exit coverage",
      text: "Clear footage at every door so you can document who enters and leaves your building.",
    },
    {
      icon: Car,
      title: "Parking lot & perimeter cameras",
      text: "Wide-area coverage of parking areas, driveways, fences, and loading zones.",
    },
    {
      icon: Warehouse,
      title: "Warehouse & inventory monitoring",
      text: "Overhead coverage of storage, staging, and shipping areas to support inventory control and loss prevention.",
    },
    {
      icon: Store,
      title: "Office & retail interiors",
      text: "Discreet interior cameras for sales floors, stock rooms, registers, and back offices.",
    },
    {
      icon: Smartphone,
      title: "Remote viewing on phone or computer",
      text: "Check live and recorded video from anywhere using an app or a web browser.",
    },
    {
      icon: RefreshCw,
      title: "Camera upgrades & replacements",
      text: "Replace aging analog or low-resolution systems with modern IP cameras, reusing cabling where it makes sense.",
    },
    {
      icon: HardDrive,
      title: "Network video recorders (NVR)",
      text: "Local recording sized for your retention needs, with storage you control on your own network.",
    },
    {
      icon: Network,
      title: "Camera network & cabling",
      text: "Structured cabling, PoE switches, and network configuration for every camera run.",
    },
  ];

  const processSteps: Array<{ title: string; text: string }> = [
    {
      title: "Site survey",
      text: "We walk your property with you, identify blind spots and priorities, and discuss what you want each camera to cover.",
    },
    {
      title: "Camera placement planning",
      text: "We map camera positions, mounting heights, and fields of view so the finished system documents what matters.",
    },
    {
      title: "Network & cabling evaluation",
      text: "We assess your existing network, PoE capacity, and cable pathways before any hardware is ordered.",
    },
    {
      title: "Installation",
      text: "Our technicians mount the cameras, run and terminate the cabling, and install the recorder in a secure location.",
    },
    {
      title: "Configuration",
      text: "We set up recording schedules, storage retention, motion events, and secure remote access.",
    },
    {
      title: "Testing",
      text: "We verify every camera's image quality, coverage, and recording before we call the job done.",
    },
    {
      title: "Walkthrough & training",
      text: "We show your team how to review footage, adjust views, and manage access on each device.",
    },
  ];

  // Factual brand strip — names are brand names, never translated.
  const brands: Array<{ name: string; logoUrl: string }> = [
    { name: "UniFi Protect", logoUrl: `${process.env.PUBLIC_URL}/brands/unifi.png` },
    { name: "Hikvision", logoUrl: `${process.env.PUBLIC_URL}/brands/hikvision.png` },
    { name: "Axis Communications", logoUrl: `${process.env.PUBLIC_URL}/brands/axis.png` },
    { name: "Dahua", logoUrl: `${process.env.PUBLIC_URL}/brands/dahua.png` },
    { name: "Amcrest", logoUrl: `${process.env.PUBLIC_URL}/brands/amcrest.png` },
    { name: "Reolink", logoUrl: `${process.env.PUBLIC_URL}/brands/reolink.png` },
  ];

  const whyChooseUs: Array<{ icon: typeof MapPin; title: string; text: string }> = [
    {
      icon: MapPin,
      title: "Local Tampa Bay Support",
      text: "A local Tampa Bay team you can call for service, adjustments, and questions — not a national call center.",
    },
    {
      icon: Camera,
      title: "Camera Placement That Actually Covers Your Site",
      text: "Every camera is placed to cover a specific area and purpose, so you get footage you can actually use.",
    },
    {
      icon: Network,
      title: "Network + Cameras + IT From One Partner",
      text: "Cabling, switches, recorders, and your business network are handled by the same team that installs the cameras.",
    },
    {
      icon: Wrench,
      title: "Responsive Service",
      text: "If a camera or recorder needs attention, you reach the people who installed it.",
    },
  ];

  return (
    // Bottom padding on mobile only, clearing the sticky call/consult bar.
    <main className="pb-24 lg:pb-0">
      <ServiceHero
        eyebrow="Serving Tampa Bay businesses"
        title="Security Camera Installation for Tampa Bay Businesses"
        subtitle="Professional network camera systems for offices, warehouses, retail locations, and job sites — coverage where it matters, viewable from anywhere."
        primaryLabel="Request a Camera Site Survey"
        onPrimary={() => {
          trackCta("camera_survey_click", "cameras_hero", "camera-installation");
          scrollToLeadForm();
        }}
        ctaName="cameras-hero"
        page="camera-installation"
      />

      <TrustBar items={trustItems} />

      {/* What we install & support */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="mx-auto max-w-2xl text-center">
          <SectionLabel>{t("What we install & support")}</SectionLabel>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            {t("Camera Systems Built Around Your Site")}
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            {t(
              "Every installation starts with how your building actually operates. We cover the areas where visibility matters most — entrances, parking, inventory, and interior spaces — instead of selling you a fixed package of cameras."
            )}
          </p>
        </div>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {whatWeInstall.map(({ icon, title, text }) => (
            <FeatureCard key={title} icon={icon} title={t(title)} text={t(text)} />
          ))}
        </div>
      </section>

      {/* Process */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mx-auto max-w-2xl text-center">
            <SectionLabel>{t("How it works")}</SectionLabel>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              {t("From Site Survey to Final Walkthrough")}
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              {t(
                "A structured process keeps the project predictable — you know what we install, where, and why before any hardware goes on a wall."
              )}
            </p>
          </div>
          <div className="mx-auto mt-12 grid max-w-5xl gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {processSteps.map(({ title, text }, index) => (
              <div
                key={title}
                className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
              >
                <div className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-sky-600 text-sm font-semibold text-white">
                  {index + 1}
                </div>
                <h3 className="mt-4 text-base font-semibold text-slate-900">
                  {t(title)}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{t(text)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brands */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="mx-auto max-w-2xl text-center">
          <SectionLabel>{t("Brands we install")}</SectionLabel>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            {t("Camera Brands We Work With")}
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            {t(
              "We are not tied to a single manufacturer. We recommend a camera brand based on your site, your budget, and the systems the cameras need to integrate with."
            )}
          </p>
        </div>
        <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {brands.map(({ name, logoUrl }) => (
            <div
              key={name}
              className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-6 shadow-sm"
            >
              <img
                src={logoUrl}
                alt={name}
                loading="lazy"
                className="h-10 w-auto max-w-full object-contain"
              />
              <span className="text-center text-sm font-medium text-slate-700">{name}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Who we help */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mx-auto max-w-2xl text-center">
            <SectionLabel>{t("Who we help")}</SectionLabel>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              {t("Cameras for Every Kind of Facility")}
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              {t(
                "We install camera systems for businesses of all sizes across Tampa Bay — from a handful of cameras at a single location to multi-building coverage."
              )}
            </p>
          </div>
          <div className="mt-10">
            <IndustryGrid
              items={[
                "Offices",
                "Warehouses",
                "Manufacturing",
                "Retail",
                "Restaurants",
                "Professional Offices",
                "Parking Areas",
              ]}
            />
          </div>
        </div>
      </section>

      {/* Internal links to related services */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="mx-auto max-w-2xl text-center">
          <SectionLabel>{t("Related services")}</SectionLabel>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            {t("One Partner for the Whole System")}
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            {t(
              "Cameras are only as reliable as the network and cabling behind them. We handle both."
            )}
          </p>
        </div>
        <div className="mx-auto mt-10 grid max-w-4xl gap-5 sm:grid-cols-2">
          <FeatureCard
            icon={Network}
            title={t("Structured cabling for camera runs")}
            text={t(
              "Clean, labeled, properly terminated cabling for every camera run — installed, tested, and documented by our own technicians."
            )}
            to="structured-cabling"
            setPage={setPage}
          />
          <FeatureCard
            icon={Camera}
            title={t("UniFi Protect installation & support")}
            text={t(
              "UniFi Protect camera systems installed and supported end to end — cameras, Network Video Recorder, network, and remote access."
            )}
            to="unifi-installer"
            setPage={setPage}
          />
        </div>
      </section>

      {/* Why SightLine */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mx-auto max-w-2xl text-center">
            <SectionLabel>{t("Why SightLine Networks")}</SectionLabel>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              {t("Why Businesses Choose SightLine for Cameras")}
            </h2>
          </div>
          <div className="mt-12">
            <WhyChooseUs items={whyChooseUs} />
          </div>
        </div>
      </section>

      {/* Real work / projects */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="mx-auto max-w-2xl text-center">
          <SectionLabel>{t("Project spotlights")}</SectionLabel>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            {t("Camera Work Around Tampa Bay")}
          </h2>
          <p className="mt-3 text-sm italic text-slate-500">
            {t("Project spotlights coming soon — real Tampa Bay project photos and details will be added here.")}
          </p>
        </div>
        {/*
          PLACEHOLDER — TODO(marketing): replace these tiles with real camera
          project photos (warehouse installs, retail interiors, NVR racks,
          technicians on-site) once supplied. Do not fill with stock imagery —
          see the handoff notes for this page for what to send over.
        */}
        <div className="mt-10 grid gap-5 sm:grid-cols-2">
          {[
            { icon: Camera, label: "Warehouse Camera Installation" },
            { icon: HardDrive, label: "Retail Camera System Upgrade" },
          ].map(({ icon: Icon, label }) => (
            <div
              key={label}
              className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center"
            >
              <Icon className="h-7 w-7 text-slate-400" />
              <span className="text-sm font-medium text-slate-500">{t(label)}</span>
              <span className="text-xs uppercase tracking-wide text-slate-400">
                {t("Photo coming soon")}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Service area */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-20 text-center">
          <SectionLabel>{t("Service area")}</SectionLabel>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            {t("Serving Businesses Throughout Tampa Bay")}
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-slate-600 leading-relaxed">
            {t("SightLine Networks provides business technology services throughout the Tampa Bay area, including:")}
          </p>
          <div className="mt-8">
            <ServiceArea cities={["Tampa", "Clearwater", "St. Petersburg", "Largo", "Pinellas Park", "Brandon"]} />
          </div>
        </div>
      </section>

      <LeadCta
        title="Ready to see what's happening at your facility?"
        text="Tell us about your building and what you want covered. We'll recommend a camera plan for your site and provide a clear quote."
        primaryLabel="Request a Camera Site Survey"
        onPrimary={() => {
          trackCta("camera_survey_click", "cameras_bottom", "camera-installation");
          scrollToLeadForm();
        }}
        ctaName="cameras-bottom"
        page="camera-installation"
      />

      {/* Lead form */}
      <section id="camera-installation-lead-form" className="mx-auto max-w-3xl scroll-mt-20 px-6 py-20">
        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
          <div className="text-center">
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              {t("Request a Camera Site Survey")}
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              {t("Fill out the form and our Tampa Bay team will follow up within one business day.")}
            </p>
          </div>
          <div className="mt-8">
            <LeadForm
              page="camera-installation"
              source="camera-installation-landing"
              formId="camera-installation-form"
              subject="Security Cameras"
              submitLabel="Request a Camera Site Survey"
            />
          </div>
        </div>
      </section>

      {/* FAQ — mirrored by the FAQPage JSON-LD rendered from cameraFaqs */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-4xl px-6 py-20">
          <div className="mb-10 max-w-2xl">
            <SectionLabel>{t("Frequently asked questions")}</SectionLabel>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              {t("Common Questions About Business Security Cameras")}
            </h2>
          </div>
          <div className="divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white">
            {cameraFaqs.map(({ q, a }) => (
              <details key={q} className="group p-6">
                <summary className="flex cursor-pointer list-none items-start justify-between gap-4 text-base font-semibold text-slate-900 marker:hidden">
                  <span>{t(q)}</span>
                  <ChevronRight className="mt-0.5 h-5 w-5 shrink-0 text-sky-600 transition-transform group-open:rotate-90" />
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{t(a)}</p>
              </details>
            ))}
          </div>
          <div className="mt-10 text-center">
            <CtaButton
              onClick={() => {
                trackCta("camera_survey_click", "cameras_faq_contact", "camera-installation");
                scrollToLeadForm();
              }}
              ctaName="cameras-faq"
              page="camera-installation"
            >
              {t("Request a Camera Site Survey")}
            </CtaButton>
          </div>
        </div>
      </section>

      <MobileStickyCta
        onConsultClick={() => {
          trackCta("camera_survey_click", "cameras_sticky", "camera-installation");
          scrollToLeadForm();
        }}
        ctaName="cameras"
        page="camera-installation"
        label="Free Security Camera Survey"
      />
    </main>
  );
}